import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsersCog,
  faPlus,
  faCheck,
  faXmark,
  faUser,
  faUserTie,
  faToggleOn,
  faToggleOff,
  faHandHoldingHeart,
  faBoxOpen,
  faSort,
  faSortUp,
  faSortDown,
  faDownload,
  faSearch,
  faExclamationTriangle,
  faArrowUp,
  faShieldAlt,
  faLock,
  faEye,
  faEyeSlash,
  faChevronLeft,
  faChevronRight,
  faCopy,
  faMapMarkerAlt,
  faIdCard,
  faGift,
  faExchangeAlt,
  faWarehouse,
  faLeaf,
  faShoppingCart,
  faChevronDown,
  faChevronUp,
  faCalendarAlt,
  faInfoCircle,
  faSpinner,
  faClock,
  faMagic,
  faHandshake,
} from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "@mui/material";
import BaseDialog from "../ui/BaseDialog";
import ActionButton from "../ui/ActionButton";
import PhoneInput from "../ui/PhoneInput";
import { useDebounce } from "../../hooks/useDebounce";
import {
  fetchOrgInfo,
  fetchAccounts,
  generateTempAccount,
  updateAccount,
  resetAccountPassword,
  promoteAccount,
  bulkUpdateAccounts,
} from "../../api/adminApi";
import { validatePassword } from "../../utils/validation";

const DEFAULT_TAB_PERMISSIONS = {
  donor: {
    donate_materials: true,
    allocate: true,
    inventory: true,
    ghg: true,
  },
  receiver: {
    browse_materials: true,
    schedule: true,
    inventory: true,
    ghg: true,
  },
  shared: {
    partnerships: true,
  },
};

const hasFormChanges = (original, current) => {
  if (!original || !current) return false;
  return JSON.stringify(original) !== JSON.stringify(current);
};

function ManageAccountsSection({ darkMode, t }) {
  const [accounts, setAccounts] = useState([]);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPromoteConfirm, setShowPromoteConfirm] = useState(false);
  const [promoteTarget, setPromoteTarget] = useState(null);
  const [editingAccount, setEditingAccount] = useState(null);
  const [editFormData, setEditFormData] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [sortConfig, setSortConfig] = useState({ key: "username", direction: "asc" });
  const [selectedIds, setSelectedIds] = useState([]);
  const [resetPassword, setResetPassword] = useState("");
  
  // UI state
  const [showPassword, setShowPassword] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isSaving, setIsSaving] = useState(false);
  const [copiedAddressId, setCopiedAddressId] = useState(null);
  const [copiedEmailId, setCopiedEmailId] = useState(null);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [credentialsToShow, setCredentialsToShow] = useState({ username: "", password: "" });
  const [copiedField, setCopiedField] = useState(null);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [togglingId, setTogglingId] = useState(null); // tracks which account is being toggled
  const [bulkToggling, setBulkToggling] = useState(false); // tracks bulk status changes
  const itemsPerPage = 10;

  const detailPanelRef = useRef(null);
  
  // debounce search for performance
  const debouncedSearch = useDebounce(searchQuery, 300);

  // load accounts and org info on mount
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [accountsData, orgData] = await Promise.all([
        fetchAccounts(),
        fetchOrgInfo(),
      ]);
      
      setAccounts(accountsData.accounts || []);
      setOrganization(orgData.organization || null);
    } catch (err) {
      console.error("Failed to load data:", err);
      setError(t("Failed to load accounts"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCopyAddress = (e, accountId, address) => {
    e.stopPropagation();
    if (!address) return;
    navigator.clipboard.writeText(address);
    setCopiedAddressId(accountId);
    setTimeout(() => setCopiedAddressId(null), 2000);
  };

  const handleCopyEmail = (e, uniqueId, email) => {
    e.stopPropagation();
    if (!email) return;
    navigator.clipboard.writeText(email);
    setCopiedEmailId(uniqueId);
    setTimeout(() => setCopiedEmailId(null), 2000);
  };

  const handleOpenInMaps = (e, address) => {
    e.stopPropagation();
    if (!address) return;
    const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // scroll to detail panel when it opens
  const scrollToDetailPanel = () => {
    setTimeout(() => {
      detailPanelRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 50);
  };

  const [generatePermissions, setGeneratePermissions] = useState({
    can_donate: true,
    can_receive: true,
    tab_permissions: {
      donor: { ...DEFAULT_TAB_PERMISSIONS.donor },
      receiver: { ...DEFAULT_TAB_PERMISSIONS.receiver },
      shared: { ...DEFAULT_TAB_PERMISSIONS.shared },
    },
  });
  const [showPermissionsPanel, setShowPermissionsPanel] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const usedAccounts = accounts.length;
  const maxAccounts = organization?.max_accounts || 10;
  const canAddMore = usedAccounts < maxAccounts;
  const usagePercent = Math.round((usedAccounts / maxAccounts) * 100);
  const activeCount = accounts.filter(a => a.is_active).length;

  // filter and sort
  const filteredAccounts = useMemo(() => {
    const searchLower = debouncedSearch.toLowerCase();
    let result = accounts.filter(acc => {
      const matchesSearch =
        acc.username.toLowerCase().includes(searchLower) ||
        acc.email.toLowerCase().includes(searchLower) ||
        (acc.company_name && acc.company_name.toLowerCase().includes(searchLower)) ||
        (acc.city && acc.city.toLowerCase().includes(searchLower));
      
      const matchesStatus = 
        filterStatus === "all" ||
        (filterStatus === "active" && acc.is_active) ||
        (filterStatus === "inactive" && !acc.is_active);

      const matchesType = 
        filterType === "all" ||
        (filterType === "company-admin" && acc.is_company_admin) ||
        (filterType === "sub-account" && !acc.is_company_admin);

      return matchesSearch && matchesStatus && matchesType;
    });

    result.sort((a, b) => {
      let aVal = sortConfig.key === "location" ? (a.address ?? "") : (a[sortConfig.key] ?? "");
      let bVal = sortConfig.key === "location" ? (b.address ?? "") : (b[sortConfig.key] ?? "");
      
      if (typeof aVal === "string" && typeof bVal === "string") {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      
      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return result;
  }, [accounts, debouncedSearch, filterStatus, filterType, sortConfig]);

  useMemo(() => {
    setCurrentPage(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, filterStatus, filterType]);

  const totalPages = Math.ceil(filteredAccounts.length / itemsPerPage);
  const paginatedAccounts = filteredAccounts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc"
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return faSort;
    return sortConfig.direction === "asc" ? faSortUp : faSortDown;
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredAccounts.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredAccounts.map(a => a.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  // generate a new temp account with one click
  const handleGenerateAccount = async () => {
    setIsGenerating(true);
    try {
      const result = await generateTempAccount({
        can_donate: generatePermissions.can_donate,
        can_receive: generatePermissions.can_receive,
        tab_permissions: generatePermissions.tab_permissions,
      });
      
      // show credentials modal with expiry info
      setCredentialsToShow({
        username: result.username,
        password: result.password,
        expiresAt: result.expires_at,
        expiresInHours: result.expires_in_hours,
      });
      setShowCredentialsModal(true);
      setShowCreateModal(false);
      
      // reload data to show new account
      await loadData();
    } catch (err) {
      console.error("Failed to generate account:", err);
      setError(t("Failed to generate account"));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleEdit = (account) => {
    setEditingAccount(account);
    setEditFormData({ ...account });
    setShowCreateModal(false); // close create panel if open
    setResetPassword(""); // clear any previous password
    setShowPassword(false);

    // also select the row
    if (!selectedIds.includes(account.id)) {
      setSelectedIds([account.id]);
    }
    scrollToDetailPanel();
  };

  const generateResetPassword = () => {
    const lower = "abcdefghijklmnopqrstuvwxyz";
    const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const numbers = "0123456789";
    const all = lower + upper + numbers;
    
    // ensure at least one of each required type
    let pass = [
      lower.charAt(Math.floor(Math.random() * lower.length)),
      upper.charAt(Math.floor(Math.random() * upper.length)),
      numbers.charAt(Math.floor(Math.random() * numbers.length)),
    ];
    
    // fill remaining chars
    for (let i = 0; i < 9; i++) {
      pass.push(all.charAt(Math.floor(Math.random() * all.length)));
    }
    
    // shuffle
    for (let i = pass.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [pass[i], pass[j]] = [pass[j], pass[i]];
    }
    
    setResetPassword(pass.join(""));
    setShowPassword(true);
  };

  const handleSetPassword = async () => {
    setIsSaving(true);
    try {
      await resetAccountPassword(editFormData.id, resetPassword);
      
      // show credentials modal
      setCredentialsToShow({
        username: editFormData?.username || "",
        password: resetPassword,
      });
      setShowCredentialsModal(true);
      
      // clear password from UI
      setResetPassword("");
      setShowPassword(false);
    } catch (err) {
      console.error("Failed to reset password:", err);
      setError(t("Failed to reset password"));
    } finally {
      setIsSaving(false);
    }
  };

  // Check if there are unsaved changes
  const hasUnsavedChanges = () => {
    return hasFormChanges(editingAccount, editFormData) || resetPassword;
  };

  // Try to close edit panel - show modal if unsaved changes
  const tryCloseEdit = (action = null) => {
    if (hasUnsavedChanges()) {
      setPendingAction(action);
      setShowUnsavedModal(true);
    } else {
      closeEditPanel();
      if (action) action();
    }
  };

  const closeEditPanel = () => {
    setEditingAccount(null);
    setEditFormData(null);
    setShowPassword(false);
    setResetPassword("");
  };

  const confirmDiscard = () => {
    setShowUnsavedModal(false);
    const action = pendingAction;
    closeEditPanel();
    setPendingAction(null);
    if (action) action();
  };

  const cancelDiscard = () => {
    setShowUnsavedModal(false);
    setPendingAction(null);
  };

  const handleCancelEdit = () => {
    tryCloseEdit();
  };

  const handleSaveEdit = async () => {
    setIsSaving(true);
    try {
      await updateAccount(editFormData.id, {
        email: editFormData.email,
        phone_number: editFormData.phone_number,
        company_name: editFormData.company_name,
        address: editFormData.address,
        city: editFormData.city,
        is_active: editFormData.is_active,
        can_donate: editFormData.can_donate,
        can_receive: editFormData.can_receive,
        tab_permissions: editFormData.tab_permissions,
      });
      
      setEditingAccount(null);
      setEditFormData(null);
      
      // reload data
      await loadData();
    } catch (err) {
      console.error("Failed to update account:", err);
      setError(t("Failed to update account"));
    } finally {
      setIsSaving(false);
    }
  };

  const toggleActive = async (accountId) => {

    // prevent spam - don't allow if already toggling
    if (togglingId || bulkToggling) return;
    
    const account = accounts.find(a => a.id === accountId);
    if (!account) return;
    
    setTogglingId(accountId);
    const startTime = Date.now();
    const MIN_DISPLAY_TIME = 600; // minimum ms to show loading state
    
    try {
      await updateAccount(accountId, { is_active: !account.is_active });
      
      // ensure minimum display time for the loading state
      const elapsed = Date.now() - startTime;
      if (elapsed < MIN_DISPLAY_TIME) {
        await new Promise(resolve => setTimeout(resolve, MIN_DISPLAY_TIME - elapsed));
      }
      
      await loadData();
    } catch (err) {
      console.error("Failed to toggle account status:", err);
      
      // ensure minimum display time even on error
      const elapsed = Date.now() - startTime;
      if (elapsed < MIN_DISPLAY_TIME) {
        await new Promise(resolve => setTimeout(resolve, MIN_DISPLAY_TIME - elapsed));
      }
      
      // check for rate limit error
      const errorMsg = err.message || String(err);
      if (errorMsg.includes("rate limit") || errorMsg.includes("limit reached") || errorMsg.includes("429")) {
        setError(t("Status change limit reached. Try again later."));
      } else {
        setError(t("Failed to update account: ") + errorMsg);
      }
    } finally {
      setTogglingId(null);
    }
  };

  // promote with confirmation
  const initiatePromote = (account) => {
    setPromoteTarget(account);
    setShowPromoteConfirm(true);
  };

  const confirmPromote = async () => {
    if (promoteTarget) {
      try {
        await promoteAccount(promoteTarget.id);

        if (editFormData && editFormData.id === promoteTarget.id) {
          setEditFormData({ ...editFormData, is_company_admin: true });
        }
        await loadData();
      } catch (err) {
        console.error("Failed to promote account:", err);
        setError(t("Failed to promote account"));
      }
    }
    setShowPromoteConfirm(false);
    setPromoteTarget(null);
  };

  const bulkSetActive = async (active) => {
    if (bulkToggling || togglingId) return;
    
    setBulkToggling(true);
    const startTime = Date.now();
    const MIN_DISPLAY_TIME = 600;
    
    try {
      await bulkUpdateAccounts(selectedIds, active);
      
      const elapsed = Date.now() - startTime;
      if (elapsed < MIN_DISPLAY_TIME) {
        await new Promise(resolve => setTimeout(resolve, MIN_DISPLAY_TIME - elapsed));
      }
      
      setSelectedIds([]);
      await loadData();
    } catch (err) {
      console.error("Failed to bulk update accounts:", err);
      
      const elapsed = Date.now() - startTime;
      if (elapsed < MIN_DISPLAY_TIME) {
        await new Promise(resolve => setTimeout(resolve, MIN_DISPLAY_TIME - elapsed));
      }
      
      const errorMsg = err.message || String(err);
      if (errorMsg.includes("rate limit") || errorMsg.includes("limit reached") || errorMsg.includes("429")) {
        setError(t("Status change limit reached for some accounts. Try again later."));
      } else {
        setError(t("Failed to update accounts: ") + errorMsg);
      }
    } finally {
      setBulkToggling(false);
    }
  };

  const exportCSV = () => {
    const headers = ["Username", "Email", "Phone", "Location", "City", "Type", "Donor", "Receiver", "Status"];
    const rows = filteredAccounts.map(acc => [
      acc.username,
      acc.email,
      acc.phone_number || "-",
      acc.company_name,
      acc.city,
      acc.is_company_admin ? "Company Admin" : "Sub-account",
      acc.can_donate ? "Yes" : "No",
      acc.can_receive ? "Yes" : "No",
      acc.is_active ? "Active" : "Inactive",
    ]);
    
    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${organization?.name || "Organization".toLowerCase()}_accounts.csv`;
    a.click();
  };

  const allSelected = filteredAccounts.length > 0 && selectedIds.length === filteredAccounts.length;
  const someSelected = selectedIds.length > 0;

  if (loading && accounts.length === 0) {
    return (
      <div className="profile-section admin-section-wide">
        <div className="admin-loading">
          <FontAwesomeIcon icon={faSpinner} spin />
          <span>{t("Loading accounts...")}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-section admin-section-wide">
      <div className="profile-section-header admin-header-with-stats">
        <div className="admin-header-left">
          <div className="profile-section-icon admin-icon company-admin">
            <FontAwesomeIcon icon={faUsersCog} />
          </div>
          <div>
            <h2 className="profile-section-title">{t("Manage Accounts")}</h2>
            <p className="profile-section-desc">
              {t("Manage users for")} <strong>{organization?.name || "Organization"}</strong>
            </p>
          </div>
        </div>
        <div className="admin-header-stats">
          <div className="admin-stats-inline">
            <div className="usage-ring">
              <svg viewBox="0 0 36 36">
                <circle
                  cx="18" cy="18" r="15.5"
                  fill="none"
                  stroke="var(--border-color)"
                  strokeWidth="3"
                />
                <circle
                  cx="18" cy="18" r="15.5"
                  fill="none"
                  stroke={usagePercent >= 90 ? "#f59e0b" : "#0d9488"}
                  strokeWidth="3"
                  strokeDasharray={`${usagePercent} 100`}
                  strokeLinecap="round"
                  transform="rotate(-90 18 18)"
                />
              </svg>
              <span className="usage-ring-text">{usagePercent}%</span>
            </div>
            <div className="usage-info">
              <span className="usage-count">{usedAccounts} <span className="usage-of">/</span> {maxAccounts}</span>
              <span className="usage-label">{t("accounts")} · {activeCount} {t("active")}</span>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="admin-error">
          <FontAwesomeIcon icon={faInfoCircle} />
          <span>{error}</span>
          <button onClick={() => setError(null)}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
      )}

      {!canAddMore && (
        <div className="admin-limit-warning">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span>{t("Account limit reached. Contact VRTTA to increase your limit.")}</span>
        </div>
      )}

      {/* toolbar */}
      <div className="admin-toolbar">
        <div className="admin-toolbar-left">
          <div className="admin-search-wrap">
            <FontAwesomeIcon icon={faSearch} className="admin-search-icon" />
            <input
              type="text"
              className="admin-search-input"
              placeholder={t("Search accounts...")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <select
            className="admin-filter-select"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="all">{t("All Types")}</option>
            <option value="company-admin">{t("Company Admins")}</option>
            <option value="sub-account">{t("Sub-accounts")}</option>
          </select>
          <select
            className="admin-filter-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">{t("All Status")}</option>
            <option value="active">{t("Active")}</option>
            <option value="inactive">{t("Inactive")}</option>
          </select>
        </div>
        <div className="admin-toolbar-right">
          <button className="admin-toolbar-btn" onClick={exportCSV} title={t("Export CSV")}>
            <FontAwesomeIcon icon={faDownload} />
          </button>
          <button 
            className="admin-toolbar-btn primary" 
            onClick={() => { setShowCreateModal(true); setEditingAccount(null); setEditFormData(null); scrollToDetailPanel(); }}
            disabled={!canAddMore}
          >
            <FontAwesomeIcon icon={faPlus} />
            <span>{t("Add Account")}</span>
          </button>
        </div>
      </div>

      {/* bulk action bar */}
      {someSelected && (
        <div className="admin-bulk-bar">
          <span className="admin-bulk-count">
            {selectedIds.length} {t("selected")}
          </span>
          <div className="admin-bulk-actions">
            <button 
              className="admin-bulk-btn success" 
              onClick={() => bulkSetActive(true)}
              disabled={bulkToggling || !!togglingId}
            >
              <FontAwesomeIcon icon={bulkToggling ? faSpinner : faToggleOn} spin={bulkToggling} />
              {bulkToggling ? t("Activating...") : t("Activate")}
            </button>
            <button 
              className="admin-bulk-btn warning" 
              onClick={() => bulkSetActive(false)}
              disabled={bulkToggling || !!togglingId}
            >
              <FontAwesomeIcon icon={bulkToggling ? faSpinner : faToggleOff} spin={bulkToggling} />
              {bulkToggling ? t("Deactivating...") : t("Deactivate")}
            </button>
            <button className="admin-bulk-btn" onClick={() => setSelectedIds([])}>
              <FontAwesomeIcon icon={faXmark} />
              {t("Clear")}
            </button>
          </div>
        </div>
      )}

      {/* table */}
      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th className="th-checkbox">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleSelectAll}
                />
              </th>
              <th className="sortable" onClick={() => handleSort("username")}>
                {t("Username")}
                <FontAwesomeIcon icon={getSortIcon("username")} className="sort-icon" />
              </th>
              <th className="sortable" onClick={() => handleSort("is_company_admin")}>
                {t("Role")}
                <FontAwesomeIcon icon={getSortIcon("is_company_admin")} className="sort-icon" />
              </th>
              <th className="sortable" onClick={() => handleSort("location")}>
                {t("Location")}
                <FontAwesomeIcon icon={getSortIcon("location")} className="sort-icon" />
              </th>
              <th className="sortable" onClick={() => handleSort("is_active")}>
                {t("Status")}
                <FontAwesomeIcon icon={getSortIcon("is_active")} className="sort-icon" />
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedAccounts.map((account) => {
              const isSelected = selectedIds.includes(account.id);
              const isEditing = editingAccount?.id === account.id;

              return (
                <tr 
                  key={account.id} 
                  className={`${!account.is_active ? "row-inactive" : ""} ${isSelected ? "row-selected" : ""} ${isEditing ? "row-editing" : ""}`}
                  onClick={() => !isEditing && handleEdit(account)}
                  style={{ cursor: "pointer" }}
                >
                  <td className="td-checkbox" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelect(account.id)}
                    />
                  </td>
                  <td>
                    <div className="cell-user">
                      <div className={`user-avatar ${account.is_company_admin ? "company-admin" : "sub-account"}`}>
                        <FontAwesomeIcon icon={account.is_company_admin ? faUserTie : faUser} />
                      </div>
                      <div className="user-info">
                        <span className="cell-primary">{account.username}</span>
                        <div className="cell-email-with-copy">
                          <span className="cell-secondary">{account.email}</span>
                          <div className="email-actions">
                            <button
                              className="address-icon-btn"
                              onClick={(e) => handleCopyEmail(e, `email-${account.id}`, account.email)}
                            >
                              <span className={`copied-tooltip ${copiedEmailId === `email-${account.id}` ? "show" : ""}`}>
                                {t("Copied!")}
                              </span>
                              <FontAwesomeIcon
                                icon={copiedEmailId === `email-${account.id}` ? faCheck : faCopy}
                                className={`address-icon ${copiedEmailId === `email-${account.id}` ? "copied" : ""}`}
                              />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="cell-role">
                      <span className={`type-badge ${account.is_company_admin ? "company-admin" : "sub-account"}`}>
                        <FontAwesomeIcon icon={account.is_company_admin ? faUserTie : faUser} />
                        {account.is_company_admin ? t("Company Admin") : t("Company User")}
                      </span>
                      {!!(account.can_donate || account.can_receive) && (
                        <span className="role-permissions">
                          {!!account.can_donate && (
                            <FontAwesomeIcon icon={faHandHoldingHeart} className="perm-icon-inline donor" title={t("Donor")} />
                          )}
                          {!!account.can_receive && (
                            <FontAwesomeIcon icon={faBoxOpen} className="perm-icon-inline receiver" title={t("Receiver")} />
                          )}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    {(() => {
                      const fullAddress = account.city ? `${account.address}, ${account.city}` : account.address;
                      const isCopied = copiedAddressId === account.id;
                      return (
                        <div className="cell-location-with-actions">
                          <div className="cell-location">
                            <span className="cell-primary">{account.address}</span>
                            <span className="cell-secondary">{account.city}</span>
                          </div>
                          <div className="address-actions">
                            <button
                              className="address-icon-btn"
                              onClick={(e) => handleCopyAddress(e, account.id, fullAddress)}
                            >
                              <span className={`copied-tooltip ${isCopied ? "show" : ""}`}>
                                {t("Copied!")}
                              </span>
                              <FontAwesomeIcon
                                icon={isCopied ? faCheck : faCopy}
                                className={`address-icon ${isCopied ? "copied" : ""}`}
                              />
                            </button>
                            <button
                              className="address-icon-btn"
                              onClick={(e) => handleOpenInMaps(e, fullAddress)}
                              title={t("Open in Google Maps")}
                            >
                              <FontAwesomeIcon
                                icon={faMapMarkerAlt}
                                className="address-icon map-icon"
                              />
                            </button>
                          </div>
                        </div>
                      );
                    })()}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {togglingId === account.id ? (
                      <span className={`status-pill ${account.is_active ? "deactivating" : "activating"}`}>
                        <FontAwesomeIcon icon={faSpinner} spin />
                        {account.is_active ? t("Deactivating...") : t("Activating...")}
                      </span>
                    ) : (
                      <button
                        className={`status-pill ${account.is_active ? "active" : "inactive"}`}
                        onClick={() => toggleActive(account.id)}
                        disabled={!!togglingId || bulkToggling}
                      >
                        <FontAwesomeIcon icon={account.is_active ? faToggleOn : faToggleOff} />
                        {account.is_active ? t("Active") : t("Inactive")}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {filteredAccounts.length === 0 && (
          <div className="admin-empty-state">
            <FontAwesomeIcon icon={faUsersCog} />
            <p>{searchQuery ? t("No accounts match your search") : t("No accounts yet")}</p>
          </div>
        )}
      </div>

      {/* table footer with pagination */}
      <div className="admin-table-footer">
        <span className="table-count">
          {t("Showing")} {Math.min((currentPage - 1) * itemsPerPage + 1, filteredAccounts.length)}-{Math.min(currentPage * itemsPerPage, filteredAccounts.length)} {t("of")} {filteredAccounts.length}
        </span>
        {totalPages > 1 && (
          <div className="pagination-controls">
            <button 
              className="pagination-btn"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <span className="pagination-info">
              {currentPage} / {totalPages}
            </span>
            <button 
              className="pagination-btn"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        )}
      </div>

      {/* Inline edit panel */}
      {editFormData && !showCreateModal && (
        <div className="admin-detail-panel" ref={detailPanelRef} style={{ scrollMarginTop: "100px" }}>
          <div className="admin-detail-header-v2">
            <div className="admin-header-left">
              <div className={`user-avatar ${editFormData.is_company_admin ? "company-admin" : "sub-account"}`}>
                <FontAwesomeIcon icon={editFormData.is_company_admin ? faUserTie : faUser} />
              </div>
              <div className="admin-header-info">
                <h3>{editFormData.username}</h3>
                <div className="admin-header-meta">
                  <span className={`type-badge small ${editFormData.is_company_admin ? "company-admin" : "sub-account"}`}>
                    {editFormData.is_company_admin ? t("Company Admin") : t("Company User")}
                  </span>
                  {!editFormData.is_company_admin && (
                    <>
                      <span className="meta-separator">•</span>
                      <button 
                        className="admin-promote-link-inline"
                        onClick={() => initiatePromote(editFormData)}
                      >
                        <FontAwesomeIcon icon={faArrowUp} />
                        {t("Promote")}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="admin-header-right">
              <div className="admin-header-status">
                <label className="toggle-switch">
                  <input 
                    type="checkbox" 
                    checked={editFormData.is_active}
                    onChange={() => setEditFormData({ ...editFormData, is_active: !editFormData.is_active })}
                  />
                  <span className="toggle-slider"></span>
                </label>
                <span className={`toggle-label ${editFormData.is_active ? "active" : "inactive"}`}>
                  {editFormData.is_active ? t("Active") : t("Inactive")}
                </span>
              </div>
              <button className="admin-detail-close" onClick={handleCancelEdit}>
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
          </div>

          <div className="admin-detail-body">
            {/* Account Information Section */}
            <div className="admin-detail-section">
              <label>
                <FontAwesomeIcon icon={faIdCard} />
                {t("Account Information")}
              </label>
              <div className="admin-detail-grid">
                <div className="admin-detail-field">
                  <label>{t("Username")}</label>
                  <input
                    type="text"
                    className="profile-input"
                    value={editFormData.username}
                    onChange={(e) => setEditFormData({ ...editFormData, username: e.target.value })}
                  />
                </div>
                <div className="admin-detail-field">
                  <label>{t("Email")}</label>
                  <input
                    type="email"
                    className="profile-input"
                    value={editFormData.email}
                    onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                  />
                </div>
                <div className="admin-detail-field">
                  <label>{t("Phone")}</label>
                  <PhoneInput
                    value={editFormData.phone_number || ""}
                    onChange={(val) => setEditFormData({ ...editFormData, phone_number: val })}
                  />
                </div>
              </div>
            </div>

            {/* Location Section */}
            <div className="admin-detail-section">
              <label>
                <FontAwesomeIcon icon={faMapMarkerAlt} />
                {t("Location")}
              </label>
              <div className="admin-detail-grid">
                <div className="admin-detail-field">
                  <label>{t("Address")}</label>
                  <input
                    type="text"
                    className="profile-input"
                    value={editFormData.address || ""}
                    onChange={(e) => setEditFormData({ ...editFormData, address: e.target.value })}
                  />
                </div>
                <div className="admin-detail-field">
                  <label>{t("City")}</label>
                  <input
                    type="text"
                    className="profile-input"
                    value={editFormData.city || ""}
                    onChange={(e) => setEditFormData({ ...editFormData, city: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/* Permissions Section */}
            <div className="admin-detail-section">
              <label>
                <FontAwesomeIcon icon={faShieldAlt} />
                {t("Permissions")}
                <Tooltip title={t("Control which features and tabs this user can access")} placement="top" arrow>
                  <span className="admin-info-icon">
                    <FontAwesomeIcon icon={faInfoCircle} />
                  </span>
                </Tooltip>
              </label>
              
              {/* Donor Permissions */}
              <div className={`admin-permission-group ${editFormData.can_donate ? "expanded" : ""}`}>
                <div 
                  className="admin-permission-header"
                  onClick={() => setEditFormData({ 
                    ...editFormData, 
                    can_donate: !editFormData.can_donate,
                    tab_permissions: !editFormData.can_donate 
                      ? { ...editFormData.tab_permissions, donor: DEFAULT_TAB_PERMISSIONS.donor }
                      : editFormData.tab_permissions
                  })}
                >
                  <div className="admin-permission-toggle">
                    <FontAwesomeIcon 
                      icon={editFormData.can_donate ? faToggleOn : faToggleOff} 
                      className={editFormData.can_donate ? "active" : ""} 
                    />
                  </div>
                  <FontAwesomeIcon icon={faHandHoldingHeart} className="admin-permission-icon donor" />
                  <span className="admin-permission-title">{t("Donor Access")}</span>
                  <FontAwesomeIcon 
                    icon={editFormData.can_donate ? faChevronUp : faChevronDown} 
                    className="admin-permission-chevron"
                  />
                </div>
                
                {editFormData.can_donate && (
                  <div className="admin-permission-tabs">
                    <label className="admin-tab-checkbox">
                      <input
                        type="checkbox"
                        checked={editFormData.tab_permissions?.donor?.donate_materials ?? true}
                        onChange={(e) => setEditFormData({
                          ...editFormData,
                          tab_permissions: {
                            ...editFormData.tab_permissions,
                            donor: { ...editFormData.tab_permissions?.donor, donate_materials: e.target.checked }
                          }
                        })}
                      />
                      <FontAwesomeIcon icon={faGift} />
                      <span>{t("Donate Materials")}</span>
                    </label>
                    <label className="admin-tab-checkbox">
                      <input
                        type="checkbox"
                        checked={editFormData.tab_permissions?.donor?.allocate ?? true}
                        onChange={(e) => setEditFormData({
                          ...editFormData,
                          tab_permissions: {
                            ...editFormData.tab_permissions,
                            donor: { ...editFormData.tab_permissions?.donor, allocate: e.target.checked }
                          }
                        })}
                      />
                      <FontAwesomeIcon icon={faExchangeAlt} />
                      <span>{t("Allocate")}</span>
                    </label>
                    <label className="admin-tab-checkbox">
                      <input
                        type="checkbox"
                        checked={editFormData.tab_permissions?.donor?.inventory ?? true}
                        onChange={(e) => setEditFormData({
                          ...editFormData,
                          tab_permissions: {
                            ...editFormData.tab_permissions,
                            donor: { ...editFormData.tab_permissions?.donor, inventory: e.target.checked }
                          }
                        })}
                      />
                      <FontAwesomeIcon icon={faWarehouse} />
                      <span>{t("Inventory")}</span>
                    </label>
                    <label className="admin-tab-checkbox">
                      <input
                        type="checkbox"
                        checked={editFormData.tab_permissions?.donor?.ghg ?? true}
                        onChange={(e) => setEditFormData({
                          ...editFormData,
                          tab_permissions: {
                            ...editFormData.tab_permissions,
                            donor: { ...editFormData.tab_permissions?.donor, ghg: e.target.checked }
                          }
                        })}
                      />
                      <FontAwesomeIcon icon={faLeaf} />
                      <span>{t("GHG Dashboard")}</span>
                    </label>
                  </div>
                )}
              </div>

              {/* Receiver Permissions */}
              <div className={`admin-permission-group ${editFormData.can_receive ? "expanded" : ""}`}>
                <div 
                  className="admin-permission-header"
                  onClick={() => setEditFormData({ 
                    ...editFormData, 
                    can_receive: !editFormData.can_receive,
                    tab_permissions: !editFormData.can_receive 
                      ? { ...editFormData.tab_permissions, receiver: DEFAULT_TAB_PERMISSIONS.receiver }
                      : editFormData.tab_permissions
                  })}
                >
                  <div className="admin-permission-toggle">
                    <FontAwesomeIcon 
                      icon={editFormData.can_receive ? faToggleOn : faToggleOff} 
                      className={editFormData.can_receive ? "active" : ""} 
                    />
                  </div>
                  <FontAwesomeIcon icon={faBoxOpen} className="admin-permission-icon receiver" />
                  <span className="admin-permission-title">{t("Receiver Access")}</span>
                  <FontAwesomeIcon 
                    icon={editFormData.can_receive ? faChevronUp : faChevronDown} 
                    className="admin-permission-chevron"
                  />
                </div>
                
                {editFormData.can_receive && (
                  <div className="admin-permission-tabs">
                    <label className="admin-tab-checkbox">
                      <input
                        type="checkbox"
                        checked={editFormData.tab_permissions?.receiver?.browse_materials ?? true}
                        onChange={(e) => setEditFormData({
                          ...editFormData,
                          tab_permissions: {
                            ...editFormData.tab_permissions,
                            receiver: { ...editFormData.tab_permissions?.receiver, browse_materials: e.target.checked }
                          }
                        })}
                      />
                      <FontAwesomeIcon icon={faShoppingCart} />
                      <span>{t("Browse Materials")}</span>
                    </label>
                    <label className="admin-tab-checkbox">
                      <input
                        type="checkbox"
                        checked={editFormData.tab_permissions?.receiver?.schedule ?? true}
                        onChange={(e) => setEditFormData({
                          ...editFormData,
                          tab_permissions: {
                            ...editFormData.tab_permissions,
                            receiver: { ...editFormData.tab_permissions?.receiver, schedule: e.target.checked }
                          }
                        })}
                      />
                      <FontAwesomeIcon icon={faCalendarAlt} />
                      <span>{t("Schedule")}</span>
                    </label>
                    <label className="admin-tab-checkbox">
                      <input
                        type="checkbox"
                        checked={editFormData.tab_permissions?.receiver?.inventory ?? true}
                        onChange={(e) => setEditFormData({
                          ...editFormData,
                          tab_permissions: {
                            ...editFormData.tab_permissions,
                            receiver: { ...editFormData.tab_permissions?.receiver, inventory: e.target.checked }
                          }
                        })}
                      />
                      <FontAwesomeIcon icon={faWarehouse} />
                      <span>{t("Inventory")}</span>
                    </label>
                    <label className="admin-tab-checkbox">
                      <input
                        type="checkbox"
                        checked={editFormData.tab_permissions?.receiver?.ghg ?? true}
                        onChange={(e) => setEditFormData({
                          ...editFormData,
                          tab_permissions: {
                            ...editFormData.tab_permissions,
                            receiver: { ...editFormData.tab_permissions?.receiver, ghg: e.target.checked }
                          }
                        })}
                      />
                      <FontAwesomeIcon icon={faLeaf} />
                      <span>{t("GHG Dashboard")}</span>
                    </label>
                  </div>
                )}
              </div>

              {/* Shared Permissions */}
              <div className="admin-permission-group expanded" style={{ marginTop: "8px" }}>
                <div className="admin-permission-header" style={{ cursor: "default" }}>
                  <FontAwesomeIcon icon={faHandshake} className="admin-permission-icon" style={{ color: "#0d9488" }} />
                  <span className="admin-permission-title">{t("Shared Features")}</span>
                </div>
                <div className="admin-permission-tabs">
                  <label className="admin-tab-checkbox">
                    <input
                      type="checkbox"
                      checked={editFormData.tab_permissions?.shared?.partnerships ?? true}
                      onChange={(e) => setEditFormData({
                        ...editFormData,
                        tab_permissions: {
                          ...editFormData.tab_permissions,
                          shared: { ...editFormData.tab_permissions?.shared, partnerships: e.target.checked }
                        }
                      })}
                    />
                    <FontAwesomeIcon icon={faHandshake} />
                    <span>{t("Partnerships")}</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Security section */}
            {(() => {
              const passwordValidation = validatePassword(resetPassword);
              const canSetPassword = passwordValidation.valid;
              
              return (
                <div className="admin-detail-section">
                  <label>
                    <FontAwesomeIcon icon={faLock} />
                    {t("Security")}
                  </label>
                  <div className="admin-password-reset">
                    <div className="admin-password-row">
                      <div className="password-input-wrapper">
                        <input
                          type={showPassword ? "text" : "password"}
                          className="profile-input"
                          value={resetPassword}
                          onChange={(e) => setResetPassword(e.target.value)}
                          placeholder={t("New temporary password")}
                        />
                        <button 
                          type="button" 
                          className="password-toggle-btn"
                          onClick={() => setShowPassword(!showPassword)}
                          title={showPassword ? t("Hide password") : t("Show password")}
                        >
                          <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                        </button>
                      </div>
                      <button type="button" className="admin-generate-btn" onClick={generateResetPassword}>
                        {t("Generate")}
                      </button>
                    </div>
                    {resetPassword && !canSetPassword && (
                      <small className="admin-input-hint warning">
                        <FontAwesomeIcon icon={faExclamationTriangle} /> {t(passwordValidation.error)}
                      </small>
                    )}
                    <div className="admin-password-actions">
                      <ActionButton 
                        onClick={handleSetPassword} 
                        variant={canSetPassword ? "warning" : "default"}
                        disabled={!canSetPassword || isSaving}
                        loading={isSaving}
                        size="small"
                      >
                        {t("Set Password")}
                      </ActionButton>
                      <small className="admin-input-hint">
                        {t("Min 8 chars, uppercase, lowercase, number")}
                      </small>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>

          {(hasFormChanges(editingAccount, editFormData) || resetPassword) && (
            <div className="admin-detail-footer">
              <ActionButton onClick={handleCancelEdit} variant="default">
                {t("Cancel")}
              </ActionButton>
              <ActionButton onClick={handleSaveEdit} variant="secondary" loading={isSaving}>
                {t("Save Changes")}
              </ActionButton>
            </div>
          )}
        </div>
      )}

      {/* inline create panel - simplified generate flow */}
      {showCreateModal && (
        <div className="admin-detail-panel" ref={detailPanelRef} style={{ scrollMarginTop: "100px" }}>
          <div className="admin-detail-header">
            <div className="admin-detail-title">
              <div className="user-avatar sub-account">
                <FontAwesomeIcon icon={faMagic} />
              </div>
              <div>
                <h3>{t("Generate Account")}</h3>
                <span>{t("Create login credentials to share with your team member")}</span>
              </div>
            </div>
            <button className="admin-detail-close" onClick={() => setShowCreateModal(false)}>
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>

          <div className="admin-detail-body">
            {/* Main action - one click generate */}
            <div className="generate-account-hero">
              <div className="generate-info">
                <FontAwesomeIcon icon={faInfoCircle} className="generate-info-icon" />
                <div className="generate-info-text">
                  <p>{t("Click below to generate login credentials.")}</p>
                  <p className="generate-info-sub">{t("The user will set their email and profile on first login. Credentials expire in 48 hours.")}</p>
                </div>
              </div>
              <ActionButton
                onClick={handleGenerateAccount}
                variant="secondary"
                size="lg"
                loading={isGenerating}
                disabled={!canAddMore}
              >
                <FontAwesomeIcon icon={faMagic} />
                {t("Generate Credentials")}
              </ActionButton>
              {!canAddMore && (
                <p className="generate-limit-warning">
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  {t("Account limit reached")} ({usedAccounts}/{maxAccounts})
                </p>
              )}
            </div>

            {/* Optional: Customize permissions */}
            <div className="admin-detail-section">
              <button 
                className="permissions-toggle-btn"
                onClick={() => setShowPermissionsPanel(!showPermissionsPanel)}
              >
                <FontAwesomeIcon icon={showPermissionsPanel ? faChevronUp : faChevronDown} />
                {t("Customize Permissions")}
                <span className="permissions-toggle-hint">{t("(optional)")}</span>
              </button>
              
              {showPermissionsPanel && (
                <div className="permissions-customize-panel">
                  {/* Donor Permissions */}
                  <div className={`admin-permission-group ${generatePermissions.can_donate ? "expanded" : ""}`}>
                    <div 
                      className="admin-permission-header"
                      onClick={() => setGeneratePermissions({ 
                        ...generatePermissions, 
                        can_donate: !generatePermissions.can_donate,
                        tab_permissions: !generatePermissions.can_donate 
                          ? { ...generatePermissions.tab_permissions, donor: DEFAULT_TAB_PERMISSIONS.donor }
                          : generatePermissions.tab_permissions
                      })}
                    >
                      <div className="admin-permission-toggle">
                        <FontAwesomeIcon 
                          icon={generatePermissions.can_donate ? faToggleOn : faToggleOff} 
                          className={generatePermissions.can_donate ? "active" : ""} 
                        />
                      </div>
                      <FontAwesomeIcon icon={faHandHoldingHeart} className="admin-permission-icon donor" />
                      <span className="admin-permission-title">{t("Donor Access")}</span>
                    </div>
                  </div>

                  {/* Receiver Permissions */}
                  <div className={`admin-permission-group ${generatePermissions.can_receive ? "expanded" : ""}`}>
                    <div 
                      className="admin-permission-header"
                      onClick={() => setGeneratePermissions({ 
                        ...generatePermissions, 
                        can_receive: !generatePermissions.can_receive,
                        tab_permissions: !generatePermissions.can_receive 
                          ? { ...generatePermissions.tab_permissions, receiver: DEFAULT_TAB_PERMISSIONS.receiver }
                          : generatePermissions.tab_permissions
                      })}
                    >
                      <div className="admin-permission-toggle">
                        <FontAwesomeIcon 
                          icon={generatePermissions.can_receive ? faToggleOn : faToggleOff} 
                          className={generatePermissions.can_receive ? "active" : ""} 
                        />
                      </div>
                      <FontAwesomeIcon icon={faBoxOpen} className="admin-permission-icon receiver" />
                      <span className="admin-permission-title">{t("Receiver Access")}</span>
                    </div>
                  </div>

                  {/* Shared Permissions */}
                  <div className="admin-permission-group expanded" style={{ marginTop: "8px" }}>
                    <div className="admin-permission-header" style={{ cursor: "default" }}>
                      <FontAwesomeIcon icon={faHandshake} className="admin-permission-icon" style={{ color: "#0d9488" }} />
                      <span className="admin-permission-title">{t("Shared Features")}</span>
                    </div>
                    <div className="admin-permission-tabs">
                      <label className="admin-tab-checkbox">
                        <input
                          type="checkbox"
                          checked={generatePermissions.tab_permissions?.shared?.partnerships ?? true}
                          onChange={(e) => setGeneratePermissions({
                            ...generatePermissions,
                            tab_permissions: {
                              ...generatePermissions.tab_permissions,
                              shared: { ...generatePermissions.tab_permissions?.shared, partnerships: e.target.checked }
                            }
                          })}
                        />
                        <FontAwesomeIcon icon={faHandshake} />
                        <span>{t("Partnerships")}</span>
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* promote confirmation modal */}
      <BaseDialog
        open={showPromoteConfirm}
        onClose={() => setShowPromoteConfirm(false)}
        title={t("Promote to Company Admin")}
        darkMode={darkMode}
        icon={{ icon: faShieldAlt, variant: "warning" }}
        maxWidth="xs"
        actions={
          <>
            <ActionButton onClick={() => setShowPromoteConfirm(false)} variant="default" modal>
              {t("Cancel")}
            </ActionButton>
            <ActionButton onClick={confirmPromote} variant="warning" modal>
              {t("Yes, Promote")}
            </ActionButton>
          </>
        }
      >
        <div className="admin-confirm-content">
          {promoteTarget && (
            <>
              <p>
                {t("Are you sure you want to promote")} <strong>{promoteTarget.username}</strong> {t("to Company Admin?")}
              </p>
              <div className="admin-confirm-warning">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <div>
                  <strong>{t("This will grant:")}</strong>
                  <ul>
                    <li>{t("Access to all company accounts")}</li>
                    <li>{t("Ability to create and edit users")}</li>
                    <li>{t("Ability to promote other users")}</li>
                    <li>{t("View company-wide data and reports")}</li>
                  </ul>
                </div>
              </div>
            </>
          )}
        </div>
      </BaseDialog>

      {/* credentials modal */}
      {showCredentialsModal && (
        <div className="credentials-modal-overlay" onClick={() => setShowCredentialsModal(false)}>
          <div className="credentials-modal" onClick={(e) => e.stopPropagation()}>
            <div className="credentials-modal-header">
              <FontAwesomeIcon icon={faCheck} className="credentials-success-icon" />
              <h3>{t("Account Created")}</h3>
              <p>{t("Share these credentials with your team member")}</p>
            </div>
            <div className="credentials-modal-body">
              {/* Expiry warning */}
              {credentialsToShow.expiresInHours && (
                <div className="credentials-expiry-warning">
                  <FontAwesomeIcon icon={faClock} />
                  <span>{t("Expires in")} {credentialsToShow.expiresInHours} {t("hours")}</span>
                </div>
              )}
              <div 
                className={`credentials-copy-box ${copiedField === "username" ? "copied" : ""}`}
                onClick={() => {
                  navigator.clipboard.writeText(credentialsToShow.username);
                  setCopiedField("username");
                  setTimeout(() => setCopiedField(null), 2000);
                }}
              >
                <div className="credentials-copy-label">{t("Username")}</div>
                <div className="credentials-copy-value">{credentialsToShow.username}</div>
                <FontAwesomeIcon icon={copiedField === "username" ? faCheck : faCopy} className="credentials-copy-icon" />
              </div>
              <div 
                className={`credentials-copy-box ${copiedField === "password" ? "copied" : ""}`}
                onClick={() => {
                  navigator.clipboard.writeText(credentialsToShow.password);
                  setCopiedField("password");
                  setTimeout(() => setCopiedField(null), 2000);
                }}
              >
                <div className="credentials-copy-label">{t("Temporary Password")}</div>
                <div className="credentials-copy-value">{credentialsToShow.password}</div>
                <FontAwesomeIcon icon={copiedField === "password" ? faCheck : faCopy} className="credentials-copy-icon" />
              </div>
              <div 
                className={`credentials-copy-box copy-all ${copiedField === "all" ? "copied" : ""}`}
                onClick={() => {
                  navigator.clipboard.writeText(`${t("Username")}: ${credentialsToShow.username}\n${t("Temporary Password")}: ${credentialsToShow.password}\n${t("Note")}: ${t("Please login and complete your profile setup within 48 hours.")}`);
                  setCopiedField("all");
                  setTimeout(() => setCopiedField(null), 2000);
                }}
              >
                <div className="credentials-copy-label">{t("Copy All")}</div>
                <div className="credentials-copy-value copy-all-preview">
                  {credentialsToShow.username} / {credentialsToShow.password}
                </div>
                <FontAwesomeIcon icon={copiedField === "all" ? faCheck : faCopy} className="credentials-copy-icon" />
              </div>
            </div>
            <div className="credentials-modal-footer">
              <p className="credentials-note">{t("The user will set their email and password on first login.")}</p>
              <ActionButton onClick={() => setShowCredentialsModal(false)} variant="secondary">
                {t("Done")}
              </ActionButton>
            </div>
          </div>
        </div>
      )}

      {/* Unsaved Changes Modal */}
      {showUnsavedModal && (
        <div className="credentials-modal-overlay" onClick={cancelDiscard}>
          <div className="unsaved-modal" onClick={(e) => e.stopPropagation()}>
            <div className="unsaved-modal-header">
              <FontAwesomeIcon icon={faExclamationTriangle} className="unsaved-warning-icon" />
              <h3>{t("Unsaved Changes")}</h3>
            </div>
            <div className="unsaved-modal-body">
              <p>{t("You have unsaved changes. Are you sure you want to discard them?")}</p>
            </div>
            <div className="unsaved-modal-footer">
              <ActionButton onClick={cancelDiscard} variant="default">
                {t("Keep Editing")}
              </ActionButton>
              <ActionButton onClick={confirmDiscard} variant="danger">
                {t("Discard Changes")}
              </ActionButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageAccountsSection;
